/**
 * The help card.
 *
 * The core is not finding the mistake — the checking does that. The core is
 * HOW the help is offered: trying must be faster and more convenient than
 * having the solution shown. Otherwise you only click „Lösung" and learn
 * nothing.
 *
 * That is why the order is fixed and the last stage is deliberately slow.
 */

import { useRef, useEffect, useLayoutEffect } from 'preact/hooks'
import { useSignal } from '@preact/signals'
import type { ComponentChildren } from 'preact'
import { Icon } from './icons.js'
import { locateError, ruleHint, isClear } from '../spell/dictionary.js'
import type { RefObject } from 'preact'
import * as dom from '../editor/text.js'
import type { Session } from '../types.js'

/** How long the solution keeps one waiting. */
const WAIT = 12000

export function Helper({
  session,
  text,
  inputRef,
  onClose,
  onApply,
  onRetry,
  onAdvance,
  helpers,
}: {
  session: Session | null
  /** The whole text — for the preview of a missing space. */
  text: string
  /** The writing area, so the card can find the word it belongs to. */
  inputRef: RefObject<HTMLDivElement>
  onClose: () => void
  onApply: (word: string) => void
  /** Select the word and let him try for himself. */
  onRetry: () => void
  onAdvance: (stage: number) => void
  /**
   * The rule functions from the editor. They stand there because they know
   * the text and the findings; the card only calls them.
   */
  helpers: {
    whatIsMissing: (hit: Session['finding']) => string
    cleanMessage: (m?: string) => string
    isWordFinding: (hit: Session['finding']) => boolean
    insertionAt: (hit: Session['finding']) => { offset: number; char: string } | null
    removalAt: (hit: Session['finding']) => { offset: number; char: string } | null
    spacingRule: (hit: Session['finding']) => string
    around: (word: string, target: string, at: number) => [string, string]
    preview: (word: string, target: string, space: boolean) => string
  }
}) {
  const card = useRef<HTMLDivElement>(null)

  /**
   * Puts the card beside the word it is about.
   *
   * It is `position: fixed` and nothing ever set `top` or `left` — so it stood
   * wherever it happened to land in the flow, which for a long text is far
   * below the window. One saw a scrollbar appear for a second and nothing
   * else: the card was open, just not on screen.
   *
   * Under the word where there is room, above it otherwise, and never past
   * the edges.
   */
  useLayoutEffect(() => {
    const c = card.current
    const el = inputRef.current
    if (!c || !el || !session) return

    const place = (): void => {
      const r = dom.rangeFor(el, session.start, session.end)
      const w = r.getBoundingClientRect()
      const box = c.getBoundingClientRect()
      const GAP = 8

      // Below the word, unless the card would fall out of the window there.
      const unten = w.bottom + GAP
      const top = unten + box.height <= innerHeight - GAP
        ? unten
        : Math.max(GAP, w.top - GAP - box.height)

      const left = Math.max(GAP, Math.min(w.left, innerWidth - box.width - GAP))
      c.style.top = `${Math.round(top)}px`
      c.style.left = `${Math.round(left)}px`
    }

    place()
    // The word moves when the window changes or the page is scrolled.
    addEventListener('resize', place)
    addEventListener('scroll', place, true)
    return () => {
      removeEventListener('resize', place)
      removeEventListener('scroll', place, true)
    }
  }, [session, inputRef])

  if (!session) return null

  const s = session
  const target = s.finding.replacements?.[0] ?? ''

  // A missing comma or space is its own kind of help.
  //
  // Not the word is wrong, the character in front of it is missing. Therefore
  // no letter stages, no "read it aloud" — only the place and the rule, then
  // the ready-made correction. The title says what it is about instead of
  // showing a correct word as if it were a mistake.
  //
  // A grammar finding without a suggestion goes the same way: LanguageTool
  // marks the whole clause and knows no ready-made correction — where the
  // comma goes depends on what was meant. The word help would put that whole
  // clause in the title and ask what you hear in it.
  const isSentenceIssue = s.punctuation || (!target && !helpers.isWordFinding(s.finding))

  return (
    <div ref={card} class="helper" role="dialog" aria-modal="false" aria-label="Hilfe zum Wort">
      <div class="helper-head">
        <span class="helper-word">
          {isSentenceIssue
            ? (s.punctuation ? helpers.whatIsMissing(s.finding) : 'Lies den Satz noch einmal')
            : s.word}
        </span>
        <button class="helper-close" onClick={onClose} aria-label="Schließen">
          <Icon name="x" />
        </button>
      </div>

      {isSentenceIssue
        ? <SentenceHelp s={s} target={target} text={text} helpers={helpers}
                        onApply={onApply} onClose={onClose} />
        : <WordHelp s={s} target={target} onApply={onApply} onRetry={onRetry}
                    onAdvance={onAdvance} cleanMessage={helpers.cleanMessage} />}
    </div>
  )
}

/** The help for a missing character or a sentence finding. */
function SentenceHelp({
  s, target, text, helpers, onApply, onClose,
}: {
  s: Session
  target: string
  text: string
  helpers: Parameters<typeof Helper>[0]['helpers']
  onApply: (w: string) => void
  onClose: () => void
}) {
  const hint = helpers.cleanMessage(s.finding.message)

  // Without a suggestion there is nothing to insert — the child must find
  // the place itself. Then only the explanation stands here.
  if (!target) {
    return (
      <>
        <div class="helper-body">{hint && <p class="hint">{hint}</p>}</div>
        <div class="helper-actions">
          <button class="action primary" onClick={onClose}>Verstanden</button>
        </div>
      </>
    )
  }

  // For a missing SPACE the character in front is taken along and every
  // space is marked: ".Aber → .␣Aber" shows the joint, while "Aber → ␣Aber"
  // shows only half of it and ". Aber" nothing at all. For a missing COMMA
  // neither is needed — the comma is visible by itself, and "␣ablenkt␣den"
  // would only be hard to read.
  const place = helpers.insertionAt(s.finding) ?? helpers.removalAt(s.finding)
  const isSpace = place?.char === ' '
  const before = isSpace && place.offset === 0 && s.start > 0
    ? text.slice(s.start - 1, s.start)
    : ''

  // Only the neighbourhood of the place, not the whole clause.
  //
  // The comma rules mark up to 77 characters; shown twice, before and after,
  // that is two lines in which one has to SEARCH for the comma — the opposite
  // of what the card is for.
  const [shownWord, shownTarget] = place
    ? helpers.around(before + s.word, before + target, before.length + place.offset)
    : [before + s.word, before + target]

  const regel = helpers.spacingRule(s.finding)

  return (
    <>
      <div class="helper-body">
        {/* LanguageTool's own sentence is dropped where we have a rule of
            our own: it says the same thing, in the polite form („Fügen Sie
            …"), to a twelve-year-old. */}
        {hint && !regel && <p class="hint">{hint}</p>}
        <p class="solution punctuation">{helpers.preview(shownWord, shownTarget, isSpace)}</p>
        {/* The rule, not just the correction. Pressing „Einsetzen" puts this
            one space right and teaches nothing — the next sentence has it
            wrong again. The rule is what carries over. */}
        {regel && <p class="helper-rule">{regel}</p>}
      </div>
      <div class="helper-actions">
        <button class="action primary" onClick={() => onApply(target)}>
          <Icon name="check" />
          <span>Einsetzen</span>
        </button>
        <button class="action" onClick={onClose}>Ich mache es selbst</button>
      </div>
    </>
  )
}

/** The help for a wrongly written word, in four stages. */
function WordHelp({
  s, target, onApply, onRetry, onAdvance, cleanMessage,
}: {
  s: Session
  target: string
  onApply: (w: string) => void
  onRetry: () => void
  onAdvance: (stage: number) => void
  cleanMessage: (m?: string) => string
}) {
  const body: ComponentChildren[] = []

  /**
   * Does the app know which word was meant?
   *
   * When it does not, the help below is skipped and the card simply asks.
   * Pointing at a letter and counting down to a reveal both claim a certainty
   * that is not there — for „Equater" the service offers „Ecuador" first, and
   * a child made to wait twelve seconds for that has been misled twice.
   */
  const clear = isClear(s.word, s.finding.replacements ?? [])

  // Stage 1: show the place.
  if (clear && s.stage >= 1 && target) {
    const loc = locateError(s.word, target)
    const mid = s.word.slice(loc.start, loc.end)
    body.push(
      <p class="spotlight">
        {s.word.slice(0, loc.start)}
        {/* If a letter is missing, the marked range is empty — then a gap. */}
        {mid ? <b>{mid}</b> : <i />}
        {s.word.slice(loc.end)}
      </p>
    )
  }

  // Stage 2: rule hint.
  //
  // The own rules are phrased as a question ("Hörst du ein langes i?") and
  // let the child think for itself. LanguageTool often explains the same
  // thing, but in the tone of a correction addressed to adults. Therefore the
  // own rule comes first; the message of the service only fills the gap when
  // no own rule applies — then it is better than a platitude.
  if (clear && s.stage >= 2) {
    // Grammatical findings (das/dass, capitalization, compound writing) bring
    // a targeted explanation with them. The own rules, by contrast, guess
    // from the letter comparison — for "das" → "dass" a hint about double
    // consonants would otherwise come, although it is about grammar.
    const isGrammar = s.finding.category !== 'TYPOS'
    const ltHint = cleanMessage(s.finding.message)
    let hint: string | undefined

    if (isGrammar && ltHint) {
      hint = ltHint
    } else {
      const own = target ? ruleHint(s.word, target) : null
      const generic = !own || own.id === 'kurz' || own.id === 'lang' || own.id === 'gleich'
      hint = generic && ltHint ? ltHint : own?.hint
    }
    if (hint) body.push(<p class="hint">{hint}</p>)
  }

  // Stage 3: the reveal.
  if (clear && s.stage >= 3 && target) {
    body.push(<p class="solution">{target}</p>)
  }

  if (!target) {
    body.push(<p>Dieses Wort kenne ich nicht. Stimmt die Schreibweise?</p>)
  }

  if (!clear && target) {
    // Not "here is the answer" but "which of these did you mean?". The
    // suggestions stand as they are, in the order the service gave them, and
    // none of them is dressed up as the right one.
    body.push(<p>Meintest du eines davon?</p>)
  } else if (s.stage === 0) {
    body.push(<p>Lies das Wort noch einmal laut. Was hörst du?</p>)
  }

  return (
    <>
      <div class="helper-body">{body}</div>
      <div class="helper-actions">
        {/* Unclear: the choices straight away, none of them singled out.
            
            No timer either — it exists so that trying is easier than being
            told, and there is nothing to try when the app itself does not
            know. */}
        {!clear && target && s.finding.replacements.slice(0, 4).map((alt) => (
          <button key={alt} class="action" onClick={() => onApply(alt)}>
            <span class="word">{alt}</span>
          </button>
        ))}
        {!clear && target && (
          <button class="action primary" onClick={onRetry}>Ich schreibe es selbst</button>
        )}

        {clear && s.stage >= 3 && target && (
          <button class="action primary" onClick={() => onApply(target)}>
            <Icon name="check" />
            <span>Einsetzen</span>
          </button>
        )}

        {clear && s.stage === 0 && (
          <>
            <button class="action primary" onClick={onRetry}>Ich probiere es selbst</button>
            {target && (
              <button class="action" onClick={() => onAdvance(1)}>Zeig mir die Stelle</button>
            )}
          </>
        )}

        {clear && s.stage === 1 && (
          <>
            <button class="action primary" onClick={onRetry}>Jetzt probiere ich</button>
            <button class="action" onClick={() => onAdvance(2)}>Gib mir einen Tipp</button>
          </>
        )}

        {clear && s.stage === 2 && (
          <>
            <button class="action primary" onClick={onRetry}>Jetzt probiere ich</button>
            <RevealButton onReveal={() => onAdvance(3)} />
          </>
        )}

        {/* Further suggestions come ONLY at the reveal stage.

            If a clickable "Schule" already stood here, the waiting time on the
            solution button would have no effect — one would get the same
            information immediately beside it. The alternatives make sense only
            once the reveal has happened anyway and the first suggestion is
            perhaps not the word meant. */}
        {clear && s.stage >= 3 &&
          s.finding.replacements.slice(1, 3).map((alt) => (
            <button key={alt} class="action" onClick={() => onApply(alt)}>
              <span class="word">{alt}</span>
            </button>
          ))}
      </div>
    </>
  )

  // There is no "Das Wort ist richtig so" any more.
  //
  // The personal dictionary knows only words, not rules — one click silenced
  // a rule for every place where the word turns up. Measured on Elmar's text:
  // "war" took the whole DOPPELTES_VERB rule out of service, and
  // "wiedersthet" and "Heimatsinsel" stood in the dictionary as correct
  // although they are wrong.
  //
  // Restricting it to spelling findings would not have been enough: exactly
  // there the wrong click is most tempting, because the wrong word looks like
  // a name that is simply unknown. Whoever really needs a name in the
  // dictionary can put it there over the word list; in the help card, at the
  // moment of the mistake, the shortcut does more harm than good.
}

/**
 * The button for the reveal — with a waiting time.
 *
 * This is the real lever: trying by yourself works immediately, the solution
 * costs twelve seconds. Not as a punishment, but so that thinking stays the
 * faster way. The bar shows that the waiting comes to an end.
 */
function RevealButton({ onReveal }: { onReveal: () => void }) {
  // The target moment stands in a ref, not in the signal: it is set once and
  // never read in order to render — only the remaining time does that.
  const until = useRef(Date.now() + WAIT)
  const left = useSignal(WAIT)

  useEffect(() => {
    const tick = (): void => {
      left.value = Math.max(0, until.current - Date.now())
    }
    const id = setInterval(tick, 250)
    tick()
    return () => clearInterval(id)
  }, [left])

  const done = left.value <= 0

  return (
    <button
      class={`action${done ? '' : ' waiting'}`}
      disabled={!done}
      onClick={onReveal}
    >
      <span>Lösung zeigen</span>
      {!done && <span class="timer">{Math.ceil(left.value / 1000)}s</span>}
      {!done && (
        <span class="bar-fill" style={{ width: `${(1 - left.value / WAIT) * 100}%` }} />
      )}
    </button>
  )
}
